// src/pages/ChurchDetails.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Stack,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ChurchIcon from '@mui/icons-material/Church';
import InfoIcon from '@mui/icons-material/Info';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GroupsIcon from '@mui/icons-material/Groups';
import { getEgliseById, getEgliseUsers, getEgliseEvents, removeUserFromEglise } from '../services/egliseService';
import { deleteEvent } from '../services/eventService';
import { Eglise, EgliseUser, Event } from '../types/church';
import CreateEventDialog from '../components/CreateEventDialog';
import EditChurchDialog from '../components/EditChurchDialog';
import EditEventDialog from '../components/EditEventDialog';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../contexts/NotificationContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`church-tabpanel-${index}`}
      aria-labelledby={`church-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ChurchDetails: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const user = authState.user;
  const { success, error: showError } = useNotification();

  const [eglise, setEglise] = useState<Eglise | null>(null);
  const [users, setUsers] = useState<EgliseUser[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Pagination for users
  const [userPage, setUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(10);

  // Dialogs state
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [openEditChurchDialog, setOpenEditChurchDialog] = useState(false);
  const [openEditEventDialog, setOpenEditEventDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Delete confirmation dialogs
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<EgliseUser | null>(null);

  // Loading states for actions
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [removingUser, setRemovingUser] = useState(false);

  // Check if user is admin of this church
  const isChurchAdmin = user?.eglises?.some(e => e.id === Number(id) && e.is_admin) || user?.role === 'SuperAdmin';

  useEffect(() => {
    fetchEgliseDetails();
  }, [id]);

  useEffect(() => {
    if (tabValue === 1 && id) {
      fetchUsers();
    } else if (tabValue === 2 && id) {
      fetchEvents();
    }
  }, [tabValue, id]);

  const fetchEgliseDetails = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const egliseData = await getEgliseById(Number(id));
      setEglise(egliseData);
      // Also fetch users to get admin count
      const usersData = await getEgliseUsers(Number(id));
      setUsers(usersData.users || []);
    } catch (err: any) {
      console.error('Failed to fetch église details:', err);
      setError(err.response?.data?.detail || t('admin.church.errorFetching'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!id) return;
    try {
      const usersData = await getEgliseUsers(Number(id));
      setUsers(usersData.users || []);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchEvents = async () => {
    if (!id) return;
    try {
      const eventsData = await getEgliseEvents(Number(id));
      setEvents(eventsData.events || []);
    } catch (err: any) {
      console.error('Failed to fetch events:', err);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEventClick = (eventId: number) => {
    navigate(`/events/${eventId}`);
  };

  const handleCreateEventSuccess = () => {
    setOpenEventDialog(false);
    fetchEvents();
    fetchEgliseDetails();
    success(t('admin.events.createSuccess'));
  };

  const handleEditChurchSuccess = () => {
    setOpenEditChurchDialog(false);
    fetchEgliseDetails();
    success(t('admin.church.updateSuccess'));
  };

  const handleEditEvent = (event: Event, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setOpenEditEventDialog(true);
  };

  const handleEditEventSuccess = () => {
    setOpenEditEventDialog(false);
    setSelectedEvent(null);
    fetchEvents();
    success(t('admin.events.updateSuccess'));
  };

  const handleDeleteEventClick = (event: Event, e: React.MouseEvent) => {
    e.stopPropagation();
    setEventToDelete(event);
    setDeleteEventDialogOpen(true);
  };

  const handleConfirmDeleteEvent = async () => {
    if (!eventToDelete) return;
    try {
      setDeletingEvent(true);
      await deleteEvent(eventToDelete.id);
      setDeleteEventDialogOpen(false);
      setEventToDelete(null);
      fetchEvents();
      fetchEgliseDetails();
      success(t('admin.events.deleteSuccess'));
    } catch (err: any) {
      console.error('Failed to delete event:', err);
      showError(err.response?.data?.detail || t('admin.events.errorDeleting'));
    } finally {
      setDeletingEvent(false);
    }
  };

  const handleRemoveUserClick = (userToRemove: EgliseUser) => {
    setUserToRemove(userToRemove);
    setRemoveUserDialogOpen(true);
  };

  const handleConfirmRemoveUser = async () => {
    if (!userToRemove || !id) return;
    try {
      setRemovingUser(true);
      await removeUserFromEglise(Number(id), userToRemove.id);
      setRemoveUserDialogOpen(false);
      setUserToRemove(null);
      fetchUsers();
      fetchEgliseDetails();
      success(t('admin.users.removeSuccess'));
    } catch (err: any) {
      console.error('Failed to remove user:', err);
      showError(err.response?.data?.detail || t('admin.users.errorRemoving'));
    } finally {
      setRemovingUser(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !eglise) {
    return <Alert severity="error">{error || t('admin.church.notFound')}</Alert>;
  }

  // Count admins
  const adminCount = users.filter(u => u.is_church_admin).length;

  return (
    <Box>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
      >
        {t('admin.church.backToList')}
      </Button>

      {/* Header with Cover */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
        {/* Cover Image */}
        <Box
          sx={{
            height: 160,
            bgcolor: eglise.cover_image_url ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: eglise.cover_image_url
              ? `url(${eglise.cover_image_url}) center/cover`
              : 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
            position: 'relative',
          }}
        >
          {/* Status Badge */}
          <Chip
            label={eglise.is_active ? t('superadmin.churches.active') : t('superadmin.churches.inactive')}
            size="small"
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: eglise.is_active ? '#dcfce7' : '#f1f5f9',
              color: eglise.is_active ? '#16a34a' : '#64748b',
              fontWeight: 600,
            }}
          />
          {isChurchAdmin && (
            <Tooltip title={t('admin.church.edit')}>
              <IconButton
                onClick={() => setOpenEditChurchDialog(true)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: eglise.is_active ? 100 : 90,
                  bgcolor: 'white',
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
                size="small"
              >
                <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Church Info */}
        <Box sx={{ p: 3, pt: 0, position: 'relative' }}>
          {/* Logo */}
          <Avatar
            src={eglise.logo_url || undefined}
            sx={{
              width: 88,
              height: 88,
              borderRadius: 2,
              border: '4px solid white',
              bgcolor: '#f1f5f9',
              mt: -6,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <ChurchIcon sx={{ fontSize: 40, color: '#64748b' }} />
          </Avatar>

          <Box sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
              <Typography variant="h4" fontWeight={700} color="#1e293b">
                {eglise.nom}
              </Typography>
              {isChurchAdmin && (
                <Chip
                  icon={<AdminPanelSettingsIcon sx={{ fontSize: 16 }} />}
                  label={t('admin.church.youAreAdmin')}
                  size="small"
                  sx={{ bgcolor: '#dbeafe', color: '#2563eb', fontWeight: 500, '& .MuiChip-icon': { color: '#2563eb' } }}
                />
              )}
            </Stack>
            {eglise.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 700 }}>
                {eglise.description}
              </Typography>
            )}

            {/* Quick Info */}
            <Stack direction="row" spacing={3} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              {eglise.address && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <LocationOnIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  <Typography variant="body2" color="text.secondary">{eglise.address}</Typography>
                </Stack>
              )}
              {eglise.admin_name && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <AdminPanelSettingsIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  <Typography variant="body2" color="text.secondary">
                    Admin: <strong>{eglise.admin_name}</strong>
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>
      </Card>

      {/* Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#dbeafe' }}>
                  <GroupsIcon sx={{ fontSize: 24, color: '#2563eb' }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="#1e293b">{eglise.user_count || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('admin.church.members')}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef3c7' }}>
                  <EventIcon sx={{ fontSize: 24, color: '#d97706' }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="#1e293b">{eglise.event_count || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('admin.church.events')}</Typography>
                  {eglise.upcoming_events !== undefined && eglise.upcoming_events > 0 && (
                    <Typography variant="caption" color="primary.main" fontWeight={500}>
                      {eglise.upcoming_events} {t('superadmin.churches.upcomingEvents')}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f3e8ff' }}>
                  <AdminPanelSettingsIcon sx={{ fontSize: 24, color: '#7c3aed' }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="#1e293b">{adminCount}</Typography>
                  <Typography variant="body2" color="text.secondary">Admins</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#dcfce7' }}>
                  <CalendarTodayIcon sx={{ fontSize: 24, color: '#16a34a' }} />
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={700} color="#1e293b">
                    {eglise.createdAt || new Date(eglise.created_at).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t('admin.church.createdAt')}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e2e8f0' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              px: 2,
              '& .MuiTab-root': {
                minHeight: 56,
                px: 2,
                color: '#64748b',
                fontSize: '0.875rem',
                '&.Mui-selected': { color: '#2563eb', fontWeight: 600 },
              },
              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: '#2563eb' },
            }}
          >
            <Tab icon={<InfoIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t('admin.tabs.information')} />
            <Tab icon={<PeopleIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={`${t('admin.tabs.users')} (${eglise.user_count || 0})`} />
            <Tab icon={<EventIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={`${t('admin.tabs.events')} (${eglise.event_count || 0})`} />
          </Tabs>
        </Box>

        {/* Information Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3 }}>
            <Grid container spacing={3}>
              {/* Contact Info */}
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0', p: 3, height: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                    <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#dbeafe' }}>
                      <PhoneIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                    </Box>
                    <Typography variant="h6" fontWeight={600} color="#1e293b">
                      {t('admin.church.contact')}
                    </Typography>
                  </Stack>

                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('admin.church.form.address')}
                      </Typography>
                      <Typography variant="body1" color="#1e293b" sx={{ mt: 0.5 }}>
                        {eglise.address || '-'}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('admin.church.form.phone')}
                      </Typography>
                      <Typography variant="body1" color="#1e293b" sx={{ mt: 0.5 }}>
                        {eglise.phone || '-'}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('admin.church.form.email')}
                      </Typography>
                      <Typography variant="body1" color="#1e293b" sx={{ mt: 0.5 }}>
                        {eglise.email || '-'}
                      </Typography>
                    </Box>
                    {eglise.website && (
                      <>
                        <Divider />
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {t('admin.church.form.website')}
                          </Typography>
                          <Typography
                            variant="body1"
                            component="a"
                            href={eglise.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'block', mt: 0.5, color: '#2563eb', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {eglise.website}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Stack>
                </Box>
              </Grid>

              {/* Details */}
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0', p: 3, height: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                    <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f3e8ff' }}>
                      <InfoIcon sx={{ fontSize: 20, color: '#7c3aed' }} />
                    </Box>
                    <Typography variant="h6" fontWeight={600} color="#1e293b">
                      {t('admin.church.details')}
                    </Typography>
                  </Stack>

                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('admin.church.form.denomination')}
                      </Typography>
                      <Typography variant="body1" color="#1e293b" sx={{ mt: 0.5 }}>
                        {eglise.denomination || '-'}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('admin.church.form.foundedYear')}
                      </Typography>
                      <Typography variant="body1" color="#1e293b" sx={{ mt: 0.5 }}>
                        {eglise.founded_year || '-'}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('admin.church.form.capacity')}
                      </Typography>
                      <Typography variant="body1" color="#1e293b" sx={{ mt: 0.5 }}>
                        {eglise.capacity ? `${eglise.capacity} ${t('admin.church.people')}` : '-'}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('admin.church.form.openingHours')}
                      </Typography>
                      <Typography variant="body1" color="#1e293b" sx={{ mt: 0.5 }}>
                        {eglise.opening_hours || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>

              {/* Admin Info */}
              {eglise.admin_name && (
                <Grid item xs={12}>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0', p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#fef3c7' }}>
                        <AdminPanelSettingsIcon sx={{ fontSize: 20, color: '#d97706' }} />
                      </Box>
                      <Typography variant="h6" fontWeight={600} color="#1e293b">
                        {t('superadmin.churches.admin')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={3} alignItems="center">
                      <Avatar sx={{ width: 48, height: 48, bgcolor: '#2563eb' }}>
                        {eglise.admin_name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight={600} color="#1e293b">{eglise.admin_name}</Typography>
                        {eglise.admin_email && (
                          <Typography variant="body2" color="text.secondary">{eglise.admin_email}</Typography>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </TabPanel>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3 }}>
            <TableContainer sx={{ maxHeight: 500, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>{t('admin.users.name')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>{t('admin.users.email')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>{t('admin.users.phone')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>{t('admin.users.role')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>{t('admin.users.churchAdmin')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>{t('admin.users.lastActivity')}</TableCell>
                    {isChurchAdmin && <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }} align="right">{t('admin.users.actions')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length > 0 ? (
                    users
                      .slice(userPage * userRowsPerPage, userPage * userRowsPerPage + userRowsPerPage)
                      .map((userItem) => (
                        <TableRow key={userItem.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar sx={{ width: 36, height: 36, bgcolor: '#2563eb', fontSize: '0.875rem' }}>
                                {userItem.name.charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography variant="body2" fontWeight={500} color="#1e293b">{userItem.name}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{userItem.email}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{userItem.phone || '-'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={userItem.role} size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 500 }} />
                          </TableCell>
                          <TableCell>
                            {userItem.is_church_admin ? (
                              <Chip
                                icon={<AdminPanelSettingsIcon sx={{ fontSize: 16 }} />}
                                label="Admin"
                                size="small"
                                sx={{ bgcolor: '#dbeafe', color: '#2563eb', fontWeight: 600, '& .MuiChip-icon': { color: '#2563eb' } }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {userItem.last_activity ? new Date(userItem.last_activity).toLocaleDateString() : '-'}
                            </Typography>
                          </TableCell>
                          {isChurchAdmin && (
                            <TableCell align="right">
                              {!userItem.is_church_admin && userItem.id !== user?.id && (
                                <Tooltip title={t('admin.users.remove')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveUserClick(userItem)}
                                    sx={{ color: '#ef4444' }}
                                  >
                                    <PersonRemoveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isChurchAdmin ? 7 : 6} align="center" sx={{ py: 8 }}>
                        <PeopleIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                          {t('admin.users.noUsers')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {users.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={users.length}
                rowsPerPage={userRowsPerPage}
                page={userPage}
                onPageChange={(_, page) => setUserPage(page)}
                onRowsPerPageChange={(e) => {
                  setUserRowsPerPage(parseInt(e.target.value, 10));
                  setUserPage(0);
                }}
                labelRowsPerPage={t('common.rowsPerPage')}
              />
            )}
          </Box>
        </TabPanel>

        {/* Events Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 3 }}>
            {isChurchAdmin && (
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenEventDialog(true)}
                  sx={{ borderRadius: 2, px: 3, py: 1 }}
                >
                  {t('admin.events.create')}
                </Button>
              </Box>
            )}

            <Grid container spacing={2}>
              {events.map((event) => {
                const eventDate = new Date(event.date);
                const isPast = eventDate < new Date();

                return (
                  <Grid item xs={12} sm={6} md={4} key={event.id}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        boxShadow: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: '#2563eb', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        opacity: isPast ? 0.7 : 1,
                      }}
                      onClick={() => handleEventClick(event.id)}
                    >
                      {/* Event Image */}
                      <Box
                        sx={{
                          height: 140,
                          bgcolor: event.image_url ? 'transparent' : '#f8fafc',
                          backgroundImage: event.image_url ? `url(${event.image_url})` : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {!event.image_url && <EventIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />}

                        {/* Date Badge */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            bgcolor: 'white',
                            borderRadius: 1.5,
                            p: 1,
                            textAlign: 'center',
                            minWidth: 50,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                        >
                          <Typography variant="h6" fontWeight={700} color="#2563eb" lineHeight={1}>
                            {eventDate.getDate()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
                            {eventDate.toLocaleDateString('fr-FR', { month: 'short' })}
                          </Typography>
                        </Box>

                        {/* Action Buttons */}
                        {isChurchAdmin && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ position: 'absolute', top: 12, right: 12 }}
                          >
                            <IconButton
                              size="small"
                              sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}
                              onClick={(e) => handleEditEvent(event, e)}
                            >
                              <EditIcon sx={{ fontSize: 16, color: '#64748b' }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#fef2f2' } }}
                              onClick={(e) => handleDeleteEventClick(event, e)}
                            >
                              <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                            </IconButton>
                          </Stack>
                        )}
                      </Box>

                      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                        <Typography variant="subtitle1" fontWeight={600} color="#1e293b" gutterBottom noWrap>
                          {event.titre}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            minHeight: 40,
                          }}
                        >
                          {event.description || '-'}
                        </Typography>

                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <ScheduleIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                            <Typography variant="body2" color="text.secondary">
                              {eventDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Stack>
                          {event.eglise_address && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LocationOnIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {event.eglise_address}
                              </Typography>
                            </Stack>
                          )}
                          {event.is_recurring && (
                            <Chip
                              label={t(`admin.events.recurrence.${event.recurrence_pattern}`)}
                              size="small"
                              sx={{ alignSelf: 'flex-start', bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 500, height: 24 }}
                            />
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {events.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8, px: 4, bgcolor: '#f8fafc', borderRadius: 2, border: '2px dashed #e2e8f0' }}>
                <EventIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" color="#1e293b" fontWeight={600} gutterBottom>
                  {t('admin.events.noEvents')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Créez votre premier événement pour commencer
                </Typography>
                {isChurchAdmin && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenEventDialog(true)}
                    sx={{ borderRadius: 2 }}
                  >
                    {t('admin.events.create')}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </TabPanel>
      </Card>

      {/* Dialogs */}
      {eglise && (
        <>
          <CreateEventDialog
            open={openEventDialog}
            onClose={() => setOpenEventDialog(false)}
            egliseId={eglise.id}
            onSuccess={handleCreateEventSuccess}
          />

          <EditChurchDialog
            open={openEditChurchDialog}
            onClose={() => setOpenEditChurchDialog(false)}
            church={eglise}
            onSuccess={handleEditChurchSuccess}
          />

          <EditEventDialog
            open={openEditEventDialog}
            onClose={() => {
              setOpenEditEventDialog(false);
              setSelectedEvent(null);
            }}
            event={selectedEvent}
            onSuccess={handleEditEventSuccess}
          />
        </>
      )}

      {/* Delete Event Confirmation Dialog */}
      <Dialog
        open={deleteEventDialogOpen}
        onClose={() => !deletingEvent && setDeleteEventDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{t('admin.events.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.events.deleteConfirmMessage', { title: eventToDelete?.titre })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setDeleteEventDialogOpen(false)} disabled={deletingEvent} sx={{ borderRadius: 1.5 }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDeleteEvent}
            color="error"
            variant="contained"
            disabled={deletingEvent}
            sx={{ borderRadius: 1.5 }}
          >
            {deletingEvent ? <CircularProgress size={20} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove User Confirmation Dialog */}
      <Dialog
        open={removeUserDialogOpen}
        onClose={() => !removingUser && setRemoveUserDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{t('admin.users.removeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.users.removeConfirmMessage', { name: userToRemove?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setRemoveUserDialogOpen(false)} disabled={removingUser} sx={{ borderRadius: 1.5 }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmRemoveUser}
            color="error"
            variant="contained"
            disabled={removingUser}
            sx={{ borderRadius: 1.5 }}
          >
            {removingUser ? <CircularProgress size={20} /> : t('admin.users.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChurchDetails;
